import {
  HStack,
  Text,
  useColorModeValue,
  Box,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { COLORS } from "../../../../utils/Colors";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import "./Navbar.css";
import { IoIosNotificationsOutline } from "react-icons/io";
import { IoMenuOutline } from "react-icons/io5";
import { HiOutlineExternalLink } from "react-icons/hi";
import { selectStore } from "../../../../store/selectors/StoreSelector";
import SettingsSwitcher from "../../../components/Settings";
import { selectUser } from "../../../../store/selectors/UserSelector";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IMAGES } from "../../../../assets/Images/Image";

export const Navbar = ({
  onMenuClick,
  isMenuOpen,
}: {
  onMenuClick: () => void;
  isMenuOpen: boolean;
}) => {
  const store = useSelector(selectStore);
  const user = useSelector(selectUser);

  const { t } = useTranslation();
  const textColor = useColorModeValue(
    COLORS.Neutral.Gray,
    COLORS.Neutral.White,
  );
  const bgColor = useColorModeValue(
    COLORS.Neutral.White,
    COLORS.Neutral.DarkGray,
  );
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");

  const navigator = useNavigate();

  const getExpiredColor = (): string => {
    if (store?.packConfig?.isTrial) return COLORS.Palette.MediumGreen;
    const cleaned = store?.packConfig?.expiredAt?.toString().replace(" at", "");
    const date = new Date(cleaned || "");
    const today = new Date();
    const diff = date.getTime() - today.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    if (days <= 0) return COLORS.Palette.Danger;
    if (days <= 3) return COLORS.Palette.Yellow;
    if (days > 3) return COLORS.Palette.MediumGreen;
    return COLORS.Neutral.Gray;
  };

  const getExpiredDate = (): string => {
    if (store?.packConfig?.isTrial) return t("common.freePlan");
    const cleaned = store?.packConfig?.expiredAt?.toString().replace(" at", "");
    const date = new Date(cleaned || "");
    return `${String(date.getDate()).padStart(2, "0")}-${String(
      date.getMonth() + 1,
    ).padStart(2, "0")}-${date.getFullYear()}`;
  };

  const handleStoreClick = () => {
    if (!store?.subdomain) return;
    navigator("https://" + store.subdomain + ".vendora-shop.online");
  };

  return (
    <Box
      className="mchic-navbar"
      bg={bgColor}
      borderBottom="1px"
      borderColor={borderColor}
      position="sticky"
      top="0"
      zIndex="sticky"
    >
      <HStack
        spacing={0}
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        height="100%"
        px={{ base: 3, md: 5 }}
      >
        {/* ── Left: actions + expiry ── */}
        <HStack spacing={1}>
          {/* Mobile menu toggle */}
          <Tooltip label={isMenuOpen ? "Close menu" : "Open menu"} hasArrow>
            <IconButton
              className="icon"
              icon={<IoMenuOutline size={22} />}
              onClick={onMenuClick}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              variant="unstyled"
              size="md"
              display={{ base: "inline-flex", md: "none" }}
            />
          </Tooltip>

          {/* Icon action strip */}
          <SettingsSwitcher />
          <LanguageSwitcher />

          <HStack className="mchic-options" spacing={{ base: 2, md: 4 }}>
            <Tooltip label="Notifications" hasArrow>
              <Box position="relative" display={{ base: "none", md: "block" }}>
                <IconButton
                  className="icon"
                  icon={<IoIosNotificationsOutline size={22} />}
                  aria-label="Notifications"
                  variant="unstyled"
                  size="md"
                />
                {/* Unread dot */}
                <Box
                  position="absolute"
                  top="7px"
                  right="8px"
                  w="7px"
                  h="7px"
                  borderRadius="full"
                  bg="red.400"
                  border="1.5px solid white"
                />
              </Box>
            </Tooltip>
          </HStack>

          {/* Expiry badge */}
          <div
            className="expired-date"
            style={{ background: getExpiredColor() }}
          >
            <p className="expired-date-text">{getExpiredDate()}</p>
          </div>
        </HStack>

        {/* ── Right: store pill ── */}
        <Box
          className="store-pill"
          onClick={handleStoreClick}
          display={{ base: "none", md: "flex" }}
          alignItems="center"
          ml={2}
          bg="blackAlpha.50"
          border="1px solid"
          borderColor="blackAlpha.100"
          cursor={store?.subdomain ? "pointer" : "default"}
          opacity={store?.subdomain ? 1 : 0.5}
        >
          <Text
            fontSize="sm"
            fontWeight="500"
            color={textColor}
            display={{ base: "none", lg: "block" }}
          >
            {store?.name || user?.email}
          </Text>
          <Box
            as={HiOutlineExternalLink}
            size={14}
            color={textColor}
            opacity={0.5}
          />
        </Box>
      </HStack>

      <div className="mobile-logo">
        <p className="store-name-for-mobile-ui">{store?.name || user?.email}</p>

        <img src={IMAGES.LOGO_MOBILE} alt="MCHIC" />
      </div>
    </Box>
  );
};
